import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from './api';

export interface Location {
  id: number;
  name: string;
  parent_location_id: number | null;
  part_count: number;
  units_on_hand: number;
  value_cents: number;
  image_url: string | null;
}

export interface LocationPartRow {
  part_id: number;
  part_number: string;
  description: string | null;
  mfg_pn: string | null;
  quantity_on_hand: number;
  value_cents: number;
  form: string | null;
}

export function useLocations(orgSlug: string | undefined) {
  return useQuery<Location[]>({
    queryKey: ['locations', orgSlug],
    queryFn: () => api.get<Location[]>(`/api/orgs/${orgSlug}/locations`),
    enabled: Boolean(orgSlug),
    staleTime: 60_000,
  });
}

export function useLocationParts(
  orgSlug: string | undefined,
  locationId: number | null,
) {
  return useQuery<LocationPartRow[]>({
    queryKey: ['location-parts', orgSlug, locationId],
    queryFn: () =>
      api.get<LocationPartRow[]>(
        `/api/orgs/${orgSlug}/locations/${locationId}/parts`,
      ),
    enabled: Boolean(orgSlug && locationId),
  });
}

export function useCreateLocation(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; parent_location_id?: number | null }) =>
      api.postJson<Location>(`/api/orgs/${orgSlug}/locations`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations', orgSlug] }),
  });
}

export function useDeleteLocation(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locationId: number) =>
      api.delete<unknown>(`/api/orgs/${orgSlug}/locations/${locationId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations', orgSlug] }),
  });
}

async function uploadLocationImage(
  orgSlug: string,
  locationId: number,
  file: File,
): Promise<Location> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(
    `/api/orgs/${orgSlug}/locations/${locationId}/image`,
    { method: 'POST', credentials: 'include', body: formData },
  );
  const text = await response.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!response.ok) {
    const detail =
      typeof body === 'object' && body !== null && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(detail, response.status, body);
  }
  return body as Location;
}

export function useUploadLocationImage(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      locationId,
      file,
    }: {
      locationId: number;
      file: File;
    }) => {
      if (!orgSlug) throw new Error('missing org slug');
      return uploadLocationImage(orgSlug, locationId, file);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['locations', orgSlug] }),
  });
}

export function useDeleteLocationImage(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locationId: number) =>
      api.delete<unknown>(
        `/api/orgs/${orgSlug}/locations/${locationId}/image`,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['locations', orgSlug] }),
  });
}
